export type BpmnActivityMarkerId =
  | 'sub-process'
  | 'loop'
  | 'multi-instance-parallel'
  | 'multi-instance-sequential'
  | 'ad-hoc'
  | 'compensation'
  | 'reset';

const PRIMARY_MARKERS: BpmnActivityMarkerId[] = [
  'loop',
  'multi-instance-parallel',
  'multi-instance-sequential',
  'ad-hoc',
  'reset',
];

const INCOMPATIBLE_PAIRS: [BpmnActivityMarkerId, BpmnActivityMarkerId][] = [
  ['multi-instance-parallel', 'loop'],
  ['multi-instance-parallel', 'reset'],
  ['multi-instance-sequential', 'loop'],
  ['multi-instance-sequential', 'reset'],
  ['multi-instance-parallel', 'multi-instance-sequential'],
  ['ad-hoc', 'loop'],
  ['ad-hoc', 'reset'],
  ['ad-hoc', 'multi-instance-parallel'],
  ['ad-hoc', 'multi-instance-sequential'],
];

const ALLOWED_3_COMBOS: BpmnActivityMarkerId[][] = [
  ['loop', 'compensation', 'sub-process'],
  ['multi-instance-parallel', 'compensation', 'sub-process'],
  ['multi-instance-sequential', 'compensation', 'sub-process'],
  ['ad-hoc', 'compensation', 'sub-process'],
].map((c) => c.slice().sort() as BpmnActivityMarkerId[]);

const normalize = (m: string): BpmnActivityMarkerId =>
  (m.toLowerCase().replace(/\s+/g, '-') === 'standard' ? 'loop' : m.toLowerCase().replace(/\s+/g, '-')) as BpmnActivityMarkerId;

const toSet = (arr: string[]): Set<string> =>
  new Set(arr.map(normalize).filter((x) => Boolean(x) && (x as string) !== 'none'));

const isIncompatible = (a: string, b: string): boolean => {
  const na = normalize(a);
  const nb = normalize(b);
  return INCOMPATIBLE_PAIRS.some(
    ([p, q]) => (na === p && nb === q) || (na === q && nb === p)
  );
};

const getPrimaryMarker = (markers: string[]): BpmnActivityMarkerId | null => {
  const set = toSet(markers);
  for (const p of PRIMARY_MARKERS) {
    if (set.has(p)) return p;
  }
  return null;
};

const isAllowed3Combo = (markers: BpmnActivityMarkerId[]): boolean => {
  if (markers.length !== 3) return false;
  const sorted = markers.map(normalize).filter((x) => Boolean(x)).sort();
  return ALLOWED_3_COMBOS.some(
    (combo) =>
      combo.length === sorted.length &&
      combo.every((c, i) => c === sorted[i])
  );
};

export function computeNextMarkers(
  current: string[],
  toAdd: string
): BpmnActivityMarkerId[] {
  const normalized = toAdd.toLowerCase().replace(/\s+/g, '-');
  if (!normalized || normalized === 'none') return current as BpmnActivityMarkerId[];
  const toAddNorm =
    normalized === 'standard' ? 'loop' : (normalized as BpmnActivityMarkerId);

  const arr = current
    .map(normalize)
    .filter((x) => Boolean(x) && (x as string) !== 'none') as BpmnActivityMarkerId[];

  const idx = arr.indexOf(toAddNorm);
  if (idx >= 0) {
    return arr.filter((_, i) => i !== idx);
  }

  const existingPrimary = getPrimaryMarker(arr);
  const toAddIsPrimary = PRIMARY_MARKERS.includes(toAddNorm);

  if (toAddIsPrimary && existingPrimary && isIncompatible(existingPrimary, toAddNorm)) {
    const withoutConflict = arr.filter((m) => m !== existingPrimary);
    return [...withoutConflict, toAddNorm];
  }

  if (arr.length < 3) {
    const candidate = [...arr, toAddNorm];
    if (candidate.length === 3 && !isAllowed3Combo(candidate)) {
      return computeNextMarkers(arr.slice(1), toAdd);
    }
    return candidate;
  }

  return computeNextMarkers(arr.slice(1), toAdd);
}
